// =====================================================================
// financeService (Financeiro F1 / S58) — chamadas axios pra API /finance/*.
// Todos exigem isAuth no backend; multi-tenant por companyId do JWT.
// =====================================================================
import api from "./api";

// [S90] Unidades do Pablo (modelo B) — lista as clinic_units da company do token.
// Companies sem unidades retornam { units: [] } (sem seletor). O `unitId` (number
// ou 'all'=Caixa Geral) é anexado às chamadas de listagem/criação abaixo quando setado.
export const listClinicUnits = async () => (await api.get("/clinic-units")).data; // { units }
const withUnit = (params, unitId) => {
  if (unitId != null && unitId !== "") params.unitId = unitId;
  return params;
};

// ----------------------------- Catálogo ------------------------------
export const listCatalog = async (search) => {
  const params = {};
  if (search) params.search = search;
  const { data } = await api.get("/finance/catalog", { params });
  return data; // { services: [{id, serviceKey, name, price, priceText, category}] }
};

// ---------------------------- Orçamentos -----------------------------
export const listQuotes = async ({ patientId, status, unitId, page = 1, pageSize = 20 } = {}) => {
  const params = { page, pageSize };
  if (patientId) params.patientId = patientId;
  if (status) params.status = status;
  withUnit(params, unitId);
  const { data } = await api.get("/finance/quotes", { params });
  return data; // { quotes, count, page, pageSize }
};
export const getQuote = async (id) => (await api.get(`/finance/quotes/${id}`)).data;
export const createQuote = async (payload) => (await api.post("/finance/quotes", payload)).data;
export const updateQuote = async (id, payload) => (await api.put(`/finance/quotes/${id}`, payload)).data;
export const approveQuote = async (id, body = {}) =>
  (await api.post(`/finance/quotes/${id}/approve`, body)).data;
export const rejectQuote = async (id) => (await api.post(`/finance/quotes/${id}/reject`)).data;

// ------------------------- Contas a receber --------------------------
export const listReceivables = async ({ status, patientId, due, unitId, page = 1, pageSize = 20 } = {}) => {
  const params = { page, pageSize };
  if (status) params.status = status;
  if (patientId) params.patientId = patientId;
  if (due) params.due = due;
  withUnit(params, unitId);
  const { data } = await api.get("/finance/receivables", { params });
  return data; // { receivables, count, page, pageSize }
};
export const receivablesSummary = async (unitId) =>
  (await api.get("/finance/receivables/summary", { params: withUnit({}, unitId) })).data;
export const cancelReceivable = async (id) => (await api.post(`/finance/receivables/${id}/cancel`)).data;

// ---------------------------- Pagamentos -----------------------------
export const payReceivable = async (id, payload) =>
  (await api.post(`/finance/receivables/${id}/pay`, payload)).data;
export const createPayment = async (payload) => (await api.post("/finance/payments", payload)).data;

// [S103] Edita um pagamento JÁ lançado (valor/forma/parcelas/pagador) — gated por tenant.
// A cascata pro caixa/cartão/comissão é feita no backend (estorna e re-lança).
export const updatePayment = async (id, payload) =>
  (await api.put(`/finance/payments/${id}`, payload)).data;

// [S103] Recebimento dividido entre vários pacientes (casal/família): 1 pagamento por
// paciente. payload = { items:[{patientId, amount}], method, unitId, notes, payerName, ... }.
export const createPaymentBatch = async (payload) =>
  (await api.post("/finance/payments/batch", payload)).data; // { payments: [...] }
// [S92] Vendedores do recebimento (dropdown "Quem fez a venda?"). Retorna { sellers: [{id,name}] }.
export const listSellers = async () => (await api.get("/finance/sellers")).data;
export const listPayments = async ({ patientId, unitId, page = 1, pageSize = 20 } = {}) => {
  const params = { page, pageSize };
  if (patientId) params.patientId = patientId;
  withUnit(params, unitId);
  const { data } = await api.get("/finance/payments", { params });
  return data;
};

// ----------------------------- Recibos -------------------------------
export const ensureReceipt = async (paymentId) =>
  (await api.post(`/finance/payments/${paymentId}/receipt`)).data; // { id, number, ... }
export const listReceiptsByPatient = async (patientId) =>
  (await api.get("/finance/receipts", { params: { patientId } })).data;

// Baixa o PDF do recibo como blob (auth-gated/Bearer) e abre em nova aba.
export const openReceiptPdf = async (receiptId) => {
  const { data } = await api.get(`/finance/receipts/${receiptId}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// ------------------- Comprovante da maquininha (S91) ----------------
// Anexa a foto/PDF do comprovante externo a um pagamento (o cliente paga na
// maquininha da clínica). NÃO confundir com o recibo fiscal (openReceiptPdf).
export const uploadPaymentReceipt = async (paymentId, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/finance/payments/${paymentId}/comprovante`, form, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return data; // { id, hasReceipt }
};
// Baixa o comprovante como blob (auth/Bearer) e abre em nova aba (imagem ou PDF).
export const openPaymentReceipt = async (paymentId) => {
  const resp = await api.get(`/finance/payments/${paymentId}/comprovante`, { responseType: "blob" });
  const type = (resp.headers && resp.headers["content-type"]) || "application/octet-stream";
  const url = window.URL.createObjectURL(new Blob([resp.data], { type }));
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};
export const deletePaymentReceipt = async (paymentId) =>
  (await api.delete(`/finance/payments/${paymentId}/comprovante`)).data;

// ---------------------------- Despesas (F2) --------------------------
export const listExpenses = async ({ status, due, unitId, page = 1, pageSize = 20 } = {}) => {
  const params = { page, pageSize };
  if (status) params.status = status;
  if (due) params.due = due;
  withUnit(params, unitId);
  const { data } = await api.get("/finance/expenses", { params });
  return data; // { expenses, count, page, pageSize }
};
export const createExpense = async (payload) => (await api.post("/finance/expenses", payload)).data;
export const updateExpense = async (id, payload) => (await api.put(`/finance/expenses/${id}`, payload)).data;
export const payExpense = async (id, payload = {}) => (await api.post(`/finance/expenses/${id}/pay`, payload)).data;
export const cancelExpense = async (id) => (await api.post(`/finance/expenses/${id}/cancel`)).data;

// [S94] Comprovante de PAGAMENTO da despesa (espelha o comprovante de pagamentos).
export const uploadExpenseReceipt = async (expenseId, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/finance/expenses/${expenseId}/comprovante`, form, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return data; // { id, hasReceipt }
};
export const openExpenseReceipt = async (expenseId) => {
  const resp = await api.get(`/finance/expenses/${expenseId}/comprovante`, { responseType: "blob" });
  const type = (resp.headers && resp.headers["content-type"]) || "application/octet-stream";
  const url = window.URL.createObjectURL(new Blob([resp.data], { type }));
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};
export const deleteExpenseReceipt = async (expenseId) =>
  (await api.delete(`/finance/expenses/${expenseId}/comprovante`)).data;

// ------------------------- Caixa / Relatórios (F2) -------------------
export const reportsSummary = async (unitId) =>
  (await api.get("/finance/reports/summary", { params: withUnit({}, unitId) })).data;
export const cashflow = async ({ from, to, unitId } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  withUnit(params, unitId);
  return (await api.get("/finance/cashflow", { params })).data;
};
export const reportsByCategory = async (kind, unitId) =>
  (await api.get("/finance/reports/by-category", { params: withUnit({ kind }, unitId) })).data;

// ----------------------------- Estoque (F2-ext) ---------------------
export const listStockProducts = async ({ search, active, unitId, page = 1, pageSize = 50 } = {}) => {
  const params = { page, pageSize };
  if (search) params.search = search;
  if (active !== undefined) params.active = active;
  withUnit(params, unitId);
  const { data } = await api.get("/finance/stock/products", { params });
  return data; // { products, count, page, pageSize }
};
export const getStockProduct = async (id) => (await api.get(`/finance/stock/products/${id}`)).data; // { product, movements }
export const createStockProduct = async (payload) => (await api.post("/finance/stock/products", payload)).data;
export const updateStockProduct = async (id, payload) => (await api.put(`/finance/stock/products/${id}`, payload)).data;
export const addStockMovement = async (id, payload) => (await api.post(`/finance/stock/products/${id}/movements`, payload)).data;
export const listStockMovements = async ({ productId, from, to, unitId, page = 1, pageSize = 50 } = {}) => {
  const params = { page, pageSize };
  if (productId) params.productId = productId;
  if (from) params.from = from;
  if (to) params.to = to;
  withUnit(params, unitId);
  return (await api.get("/finance/stock/movements", { params })).data;
};
export const stockAlerts = async (unitId) =>
  (await api.get("/finance/stock/alerts", { params: withUnit({}, unitId) })).data; // { lowStock, expiring }

// Estoque Fase 2 — lotes (saldo líquido + validade) + fornecedores
export const listStockLots = async (productId) => {
  const params = {};
  if (productId) params.productId = productId;
  return (await api.get("/finance/stock/lots", { params })).data; // { lots }
};
export const listStockSuppliers = async (active) => {
  const params = {};
  if (active !== undefined) params.active = active;
  return (await api.get("/finance/stock/suppliers", { params })).data; // { suppliers }
};
export const createStockSupplier = async (payload) => (await api.post("/finance/stock/suppliers", payload)).data;
export const updateStockSupplier = async (id, payload) => (await api.put(`/finance/stock/suppliers/${id}`, payload)).data;

// Estoque Fase 3b — kits por procedimento (insumos + margem)
export const listStockKits = async () => (await api.get("/finance/stock/kits")).data; // { kits:[{id,name,price,items,cost,margin}] }
export const createStockKit = async (payload) => (await api.post("/finance/stock/kits", payload)).data;
export const updateStockKit = async (id, payload) => (await api.put(`/finance/stock/kits/${id}`, payload)).data;
export const deleteStockKit = async (id) => (await api.delete(`/finance/stock/kits/${id}`)).data;
export const applyStockKit = async (id, payload) => (await api.post(`/finance/stock/kits/${id}/apply`, payload)).data;

// ----------------------------- Comissões (F2-ext) -------------------
export const listCommissionRules = async () => (await api.get("/finance/commission-rules")).data; // { rules, professionals }
export const upsertCommissionRule = async (payload) => (await api.post("/finance/commission-rules", payload)).data;
export const deleteCommissionRule = async (id) => (await api.delete(`/finance/commission-rules/${id}`)).data;
export const listCommissions = async ({ from, to, professionalId, status, page = 1, pageSize = 50 } = {}) => {
  const params = { page, pageSize };
  if (from) params.from = from;
  if (to) params.to = to;
  if (professionalId) params.professionalId = professionalId;
  if (status) params.status = status;
  return (await api.get("/finance/commissions", { params })).data; // { commissions, count }
};
export const commissionsSummary = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return (await api.get("/finance/commissions/summary", { params })).data; // { byProfessional }
};
export const payCommission = async (id) => (await api.post(`/finance/commissions/${id}/pay`)).data;
export const reconcileCommissions = async () => (await api.post("/finance/commissions/reconcile")).data;

// ------------------- Comissão Instituto Melo (Pablo) — S71 ----------
// Endpoints isolados /finance/melo/* (só o tenant Pablo usa; a aba é gated por company).
export const listMeloTiers = async () => (await api.get("/finance/melo/tiers")).data; // { tiers }
export const upsertMeloTier = async (payload) => (await api.post("/finance/melo/tiers", payload)).data;
export const deleteMeloTier = async (id) => (await api.delete(`/finance/melo/tiers/${id}`)).data;
export const listMeloFixed = async () => (await api.get("/finance/melo/fixed")).data; // { fixed }
export const upsertMeloFixed = async (payload) => (await api.post("/finance/melo/fixed", payload)).data;
export const deleteMeloFixed = async (id) => (await api.delete(`/finance/melo/fixed/${id}`)).data;
export const listMeloContracts = async (patientId) => {
  const params = {};
  if (patientId) params.patientId = patientId;
  return (await api.get("/finance/melo/contracts", { params })).data; // { contracts }
};
export const upsertMeloContract = async (payload) => (await api.post("/finance/melo/contracts", payload)).data;
export const deleteMeloContract = async (id) => (await api.delete(`/finance/melo/contracts/${id}`)).data;
export const listMeloCommissions = async ({ from, to, kind, status, page = 1, pageSize = 50 } = {}) => {
  const params = { page, pageSize };
  if (from) params.from = from;
  if (to) params.to = to;
  if (kind) params.kind = kind;
  if (status) params.status = status;
  return (await api.get("/finance/melo/commissions", { params })).data; // { commissions, count }
};
export const meloCommissionsSummary = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return (await api.get("/finance/melo/commissions/summary", { params })).data; // { byPerson }
};
export const payMeloCommission = async (id) => (await api.post(`/finance/melo/commissions/${id}/pay`)).data;
export const reconcileMeloCommissions = async () => (await api.post("/finance/melo/commissions/reconcile")).data;

// ------------------- Painel / Metas Instituto Melo (Pablo) — S72 ----------
// Caixa do dia (+ metas embutidas) · Metas 5 níveis · Relatório por pessoa. Gated company 3.
export const meloCaixaDoDia = async (date, unitId) => {
  const params = {};
  if (date) params.date = date;
  withUnit(params, unitId); // [S90 F5] caixa/metas por unidade
  return (await api.get("/finance/melo/caixa", { params })).data; // { dia, hoje, mes, metas, serieDiaria }
};
export const meloRelatorioPessoa = async ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return (await api.get("/finance/melo/relatorio-pessoa", { params })).data; // { periodo, equipe, medicos, totais }
};
export const getMetaBaseline = async (year, month, unitId) =>
  (await api.get("/finance/melo/metas/baseline", { params: withUnit({ year, month }, unitId) })).data; // { baseline }
export const upsertMetaBaseline = async (payload) => (await api.post("/finance/melo/metas/baseline", payload)).data;
export const listMetaTiers = async () => (await api.get("/finance/melo/metas/tiers")).data; // { tiers }
export const upsertMetaTier = async (payload) => (await api.post("/finance/melo/metas/tiers", payload)).data;
export const deleteMetaTier = async (id) => (await api.delete(`/finance/melo/metas/tiers/${id}`)).data;

// ----------------------------- Tesouraria (S63) ---------------------
export const listAccounts = async (unitId) =>
  (await api.get("/finance/accounts", { params: withUnit({}, unitId) })).data; // { accounts }
export const createAccount = async (payload) => (await api.post("/finance/accounts", payload)).data;
export const updateAccount = async (id, payload) => (await api.put(`/finance/accounts/${id}`, payload)).data;
export const getAccountEntries = async (id, { from, to } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return (await api.get(`/finance/accounts/${id}/entries`, { params })).data; // { account, entries, totals... }
};
export const createAccountEntry = async (payload) => (await api.post("/finance/entries", payload)).data;
export const transferAccounts = async (payload) => (await api.post("/finance/transfer", payload)).data;
export const reconcileEntries = async () => (await api.post("/finance/entries/reconcile")).data; // { credits, debits }

export const ACCOUNT_TYPES = [
  { value: "caixa", label: "Caixa (dinheiro)" },
  { value: "banco", label: "Conta bancária" },
  { value: "cartao", label: "Maquininha / cartão" },
  { value: "poupanca", label: "Poupança" },
  { value: "outro", label: "Outro" }
];

// ----------------------------- Conciliação (S63 Build B) ------------
export const importStatement = async (accountId, content) =>
  (await api.post("/finance/reconciliation/import", { accountId, content })).data; // { imported, matched, pending, skipped }
export const listStatementLines = async ({ accountId, status } = {}) => {
  const params = { accountId };
  if (status) params.status = status;
  return (await api.get("/finance/reconciliation", { params })).data; // { lines, summary }
};
export const statementCandidates = async (lineId) =>
  (await api.get(`/finance/reconciliation/lines/${lineId}/candidates`)).data; // { candidates }
export const matchStatementLine = async (lineId, entryId) =>
  (await api.post(`/finance/reconciliation/lines/${lineId}/match`, { entryId })).data;
export const unmatchStatementLine = async (lineId) =>
  (await api.post(`/finance/reconciliation/lines/${lineId}/unmatch`)).data;
export const ignoreStatementLine = async (lineId) =>
  (await api.post(`/finance/reconciliation/lines/${lineId}/ignore`)).data;

// ----------------------------- Cartões (S63) ------------------------
export const listCardFeeRules = async () => (await api.get("/finance/card/fee-rules")).data; // { rules }
export const upsertCardFeeRule = async (payload) => (await api.post("/finance/card/fee-rules", payload)).data;
export const deleteCardFeeRule = async (id) => (await api.delete(`/finance/card/fee-rules/${id}`)).data;
export const listCardReceivables = async ({ from, to, status } = {}) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (status) params.status = status;
  return (await api.get("/finance/card/receivables", { params })).data; // { receivables, summary }
};
export const receiveCardParcel = async (id, accountId) =>
  (await api.post(`/finance/card/receivables/${id}/receive`, { accountId })).data;
export const unreceiveCardParcel = async (id) =>
  (await api.post(`/finance/card/receivables/${id}/unreceive`)).data;

export const CARD_BRANDS = [
  { value: "todas", label: "Todas as bandeiras" },
  { value: "visa", label: "Visa" },
  { value: "master", label: "Mastercard" },
  { value: "elo", label: "Elo" },
  { value: "amex", label: "Amex" },
  { value: "hipercard", label: "Hipercard" },
  { value: "outro", label: "Outro" }
];

export const EXPENSE_METHODS = [
  { value: "dinheiro", label: "Dinheiro" },
  { value: "pix", label: "PIX" },
  { value: "debito", label: "Cartão de débito" },
  { value: "credito", label: "Cartão de crédito" },
  { value: "transferencia", label: "Transferência" },
  { value: "boleto", label: "Boleto" }
];
export const EXPENSE_STATUS = {
  pendente: { label: "Pendente", color: "#b26a00" },
  pago: { label: "Pago", color: "#2e7d32" },
  cancelado: { label: "Cancelado", color: "#757575" }
};

// --------------------------- Estoque / Comissões (F2-ext) ------------
export const STOCK_MOVE_TYPES = [
  { value: "entrada", label: "Entrada (compra / reposição)" },
  { value: "saida", label: "Saída (uso / venda)" },
  { value: "ajuste", label: "Ajuste (definir saldo contado)" }
];
export const STOCK_UNITS = ["un", "frasco", "ml", "seringa", "ampola", "caixa"];
export const COMMISSION_STATUS = {
  apurada: { label: "A pagar", color: "#b26a00" },
  paga: { label: "Paga", color: "#2e7d32" },
  cancelada: { label: "Cancelada", color: "#757575" }
};

// ----------------------------- Helpers -------------------------------
export const PAYMENT_METHODS = [
  { value: "dinheiro", label: "Dinheiro" },
  { value: "pix", label: "PIX" },
  { value: "debito", label: "Cartão de débito" },
  { value: "credito", label: "Cartão de crédito" },
  { value: "convenio", label: "Convênio" },
  { value: "transferencia", label: "Transferência" },
  { value: "cortesia", label: "Cortesia" }
];

export const RECEIVABLE_STATUS = {
  pendente: { label: "Pendente", color: "#b26a00" },
  parcial: { label: "Parcial", color: "#1565c0" },
  pago: { label: "Pago", color: "#2e7d32" },
  atrasado: { label: "Atrasado", color: "#c62828" },
  cancelado: { label: "Cancelado", color: "#757575" }
};

export const QUOTE_STATUS = {
  rascunho: { label: "Rascunho", color: "#757575" },
  enviado: { label: "Enviado", color: "#1565c0" },
  aprovado: { label: "Aprovado", color: "#2e7d32" },
  recusado: { label: "Recusado", color: "#c62828" },
  expirado: { label: "Expirado", color: "#b26a00" }
};

export const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

export const financeErrorMessage = (err) => {
  const code = err && err.response && err.response.data && err.response.data.error;
  const map = {
    ERR_FIN_PATIENT_REQUIRED: "Selecione o paciente.",
    ERR_FIN_PATIENT_NOT_FOUND: "Paciente não encontrado.",
    ERR_FIN_QUOTE_EMPTY: "Adicione ao menos um item ao orçamento.",
    ERR_FIN_QUOTE_NOT_FOUND: "Orçamento não encontrado.",
    ERR_FIN_QUOTE_LOCKED: "Orçamento já fechado não pode ser editado.",
    ERR_FIN_QUOTE_ALREADY_APPROVED: "Orçamento já foi aprovado.",
    ERR_FIN_QUOTE_ZERO_TOTAL: "O total do orçamento precisa ser maior que zero.",
    ERR_FIN_RECEIVABLE_NOT_FOUND: "Conta a receber não encontrada.",
    ERR_FIN_RECEIVABLE_ALREADY_PAID: "Essa conta já está quitada.",
    ERR_FIN_RECEIVABLE_CANCELLED: "Essa conta está cancelada.",
    ERR_FIN_RECEIVABLE_HAS_PAYMENT: "Não dá pra cancelar: já houve pagamento.",
    ERR_FIN_PAYMENT_AMOUNT_INVALID: "Informe um valor válido.",
    ERR_FIN_PAYMENT_METHOD_INVALID: "Selecione uma forma de pagamento válida.",
    ERR_FIN_PAYMENT_EXCEEDS_REMAINING: "O valor excede o saldo da conta.",
    ERR_FIN_PAYMENT_EDIT_DISABLED: "Edição de pagamento não está habilitada para esta clínica.",
    ERR_FIN_PAYMENT_EDIT_HAS_NFSE: "Já existe NF-e autorizada para este pagamento. Cancele a nota antes de corrigir valor/forma.",
    ERR_FIN_PAYMENT_EDIT_CARD_RECEIVED: "Há repasse de cartão já recebido neste pagamento. Estorne a parcela (aba Cartões) antes de editar.",
    ERR_FIN_PAYMENT_EDIT_COMMISSION_PAID: "Há comissão já paga sobre este pagamento. Acerte a comissão antes de editar.",
    ERR_FIN_PAYMENT_EDIT_RECONCILED: "A entrada deste pagamento já foi conciliada no banco. Desfaça a conciliação antes de editar.",
    ERR_FIN_BATCH_EMPTY: "Adicione ao menos um paciente com valor.",
    ERR_FIN_BATCH_TOO_MANY: "Muitos pacientes num recebimento só (máximo 12).",
    ERR_FIN_RECEIPT_NOT_FOUND: "Recibo não encontrado.",
    ERR_FIN_COMPROVANTE_FILE_REQUIRED: "Selecione a foto do comprovante.",
    ERR_FIN_COMPROVANTE_NOT_FOUND: "Comprovante não encontrado.",
    ERR_UPLOAD_MIMETYPE: "Arquivo inválido. Envie uma foto (JPG/PNG/WEBP) ou PDF.",
    ERR_UPLOAD_CONTEXT: "Não foi possível anexar agora. Tente novamente.",
    ERR_FIN_EXPENSE_AMOUNT_INVALID: "Informe um valor válido.",
    ERR_FIN_EXPENSE_DUEDATE_REQUIRED: "Informe o vencimento.",
    ERR_FIN_EXPENSE_NOT_FOUND: "Despesa não encontrada.",
    ERR_FIN_EXPENSE_METHOD_INVALID: "Forma de pagamento inválida.",
    ERR_FIN_STOCK_NAME_REQUIRED: "Informe o nome do produto.",
    ERR_FIN_STOCK_PRODUCT_NOT_FOUND: "Produto não encontrado.",
    ERR_FIN_STOCK_TYPE_INVALID: "Tipo de movimento inválido.",
    ERR_FIN_STOCK_QTY_INVALID: "Informe uma quantidade válida.",
    ERR_FIN_STOCK_INSUFFICIENT: "Saldo insuficiente em estoque pra essa saída.",
    ERR_FIN_COMM_PROF_REQUIRED: "Selecione o profissional.",
    ERR_FIN_COMM_PERCENT_INVALID: "O percentual deve estar entre 0 e 100.",
    ERR_FIN_COMM_RULE_NOT_FOUND: "Regra de comissão não encontrada.",
    ERR_FIN_COMMISSION_NOT_FOUND: "Comissão não encontrada.",
    ERR_FIN_COMMISSION_NOT_PENDING: "Essa comissão não está em aberto (apurada).",
    ERR_FIN_ACCOUNT_NAME_REQUIRED: "Informe o nome da conta.",
    ERR_FIN_ACCOUNT_NOT_FOUND: "Conta não encontrada.",
    ERR_FIN_ENTRY_TYPE_INVALID: "Selecione crédito ou débito.",
    ERR_FIN_ENTRY_AMOUNT_INVALID: "Informe um valor válido.",
    ERR_FIN_TRANSFER_ACCOUNTS_REQUIRED: "Selecione as contas de origem e destino.",
    ERR_FIN_TRANSFER_SAME_ACCOUNT: "Origem e destino devem ser contas diferentes.",
    ERR_FIN_OFX_EMPTY: "Não encontrei transações nesse arquivo OFX.",
    ERR_FIN_STMT_LINE_NOT_FOUND: "Linha do extrato bancário não encontrada.",
    ERR_FIN_ENTRY_NOT_FOUND: "Lançamento do sistema não encontrado.",
    ERR_FIN_CARD_KIND_INVALID: "Selecione débito ou crédito.",
    ERR_FIN_CARD_FEE_INVALID: "A taxa deve estar entre 0 e 100.",
    ERR_FIN_CARD_RULE_NOT_FOUND: "Regra de taxa não encontrada.",
    ERR_FIN_CARD_PARCEL_NOT_FOUND: "Parcela do cartão não encontrada.",
    ERR_FIN_META_NAME_REQUIRED: "Informe o nome da meta.",
    ERR_FIN_META_PCT_INVALID: "O percentual da meta deve ser ≥ 0.",
    ERR_FIN_META_TIER_NOT_FOUND: "Meta não encontrada.",
    ERR_FIN_META_PERIOD_INVALID: "Informe um mês e ano válidos.",
    ERR_FIN_META_BASE_INVALID: "Informe uma base válida (≥ 0).",
    // [S90] Unidades do Pablo (modelo B)
    ERR_UNIT_REQUIRED: "Selecione uma unidade específica para cadastrar (o Caixa Geral é só consolidação).",
    ERR_UNIT_INVALID: "Unidade inválida para esta clínica.",
    ERR_UNIT_SCOPE_ADMIN_ONLY: "Somente administradores podem ver o Caixa Geral (consolidado).",
    ERR_FISCAL_EMITENTE_NOT_CONFIGURED: "Esta unidade não tem emitente fiscal configurado (NF).",
    ERR_FISCAL_TOMADOR_CPF_REQUIRED: "O paciente precisa ter CPF cadastrado para emitir a nota.",
    ERR_FISCAL_ALREADY_AUTHORIZED: "Este pagamento já tem nota autorizada.",
    ERR_FISCAL_TOKEN_MISSING: "Token da Focus não configurado no servidor (avise o suporte).",
    ERR_FISCAL_NOT_AUTHORIZED: "A nota precisa estar autorizada para esta ação.",
    ERR_FISCAL_JUSTIFICATIVA_INVALID: "A justificativa do cancelamento deve ter entre 15 e 255 caracteres."
  };
  if (code && map[code]) return map[code];
  if (code && typeof code === "string" && !code.startsWith("ERR_")) return code;
  return "Não foi possível concluir a operação. Tente novamente.";
};

// ==================== Fiscal — NF-e/NFS-e (Focus) / S97 ====================
// Emissão de nota fiscal de serviço (piloto Pablo). Gated: o botão só aparece se a
// empresa tem emitente configurado (listFiscalEmitentes vazio => feature invisível).
export const listFiscalEmitentes = async () =>
  (await api.get("/finance/fiscal/emitentes")).data; // { emitentes: [...] }
// Onboarding: cadastra a clínica na Focus (multipart: campos + arquivo "cert" .pfx).
export const createFiscalEmitente = async (formData) =>
  (await api.post("/finance/fiscal/emitentes", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  })).data;
export const deleteFiscalEmitente = async (id) =>
  (await api.delete(`/finance/fiscal/emitentes/${id}`)).data;
// [S100] update LOCAL da config fiscal (IBPT/alíquota/discriminação) — sem Focus nem re-subir cert.
export const updateFiscalEmitenteConfig = async (id, data) =>
  (await api.put(`/finance/fiscal/emitentes/${id}/config`, data)).data;
export const listFiscalNotes = async (paymentId) =>
  (await api.get("/finance/fiscal/notes", { params: paymentId ? { paymentId } : {} })).data; // { notes: [...] }
export const emitNfse = async (paymentId, discriminacao) =>
  (await api.post("/finance/fiscal/nfse", { paymentId, discriminacao })).data; // note
export const refreshFiscalNote = async (id) =>
  (await api.post(`/finance/fiscal/notes/${id}/refresh`)).data;
export const cancelFiscalNote = async (id, justificativa) =>
  (await api.delete(`/finance/fiscal/notes/${id}`, { data: { justificativa } })).data;
// PDF/XML servidos por PROXY autenticado (blob) — não expõe a URL do S3 (LGPD).
// baixa o arquivo com um nome AMIGÁVEL (pra enviar ao cliente) — não a hash do S3.
const baixarBlob = (data, mime, filename) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};
export const openFiscalPdf = async (id, filename) => {
  const { data } = await api.get(`/finance/fiscal/notes/${id}/pdf`, { responseType: "blob" });
  baixarBlob(data, "application/pdf", filename || "NFS-e.pdf");
};
export const openFiscalXml = async (id, filename) => {
  const { data } = await api.get(`/finance/fiscal/notes/${id}/xml`, { responseType: "blob" });
  baixarBlob(data, "application/xml", filename || "NFS-e.xml");
};
